<script setup lang="ts">
import { computed } from "vue";
import { format } from "date-fns";
import { z } from "zod";
import { MeasurementUnitLength, MeasurementUnitWeight, SmokerStatus } from "@/stores/aboutYou";
import { MedicalCondition } from "@/entities/medicalValidator";
import {
    footInchesToCentimetres,
    centimetresToFootInches,
    stonePoundsToKilograms,
    kilogramsToStonePounds,
    inchesToCentimetres,
    centimetresToInches,
    convertInchesToFootAndInches,
    convertPoundsToStoneAndPounds,
} from "@/utils/quote";
import { type ActivityLabelGroup, DailyActivitiesLabels } from "@/stores/dailyActivities";
import { Height, QuoteRequest, Waist, Weight, Lifestyle } from "@/entities/quote";
import { formatCurrency as formatTotalFund } from "../composables/formatCurrency";
import { PaySurvivingDependantType } from "@/stores/pensionOptions";
import { groupBy } from "@/utils";
import { RelationshipToCustomer } from "@/stores/dependantAboutYou";

const props = defineProps<{ quote: QuoteRequest }>();

const applicantSpouseDependant = computed(() => {
    if (props.quote.product.paySurvivingDependant.type === PaySurvivingDependantType.Fraction) {
        return `yes ${props.quote.product.paySurvivingDependant.value}%`;
    } else {
        return "no";
    }
});

const quoteEscalationType = computed(() => {
    const type = props.quote.product.escalation.type.replace(/-/g, " ");
    return type === "none" ? "level" : type;
});

function YesNo(answer: boolean | undefined | null) {
    if (answer === undefined || answer === null) {
        return "";
    }

    return answer ? "Yes" : "No";
}

function CalculateHeightLine(height: Height) {
    if (height?.unit === MeasurementUnitLength.Inches) {
        const feetInches = convertInchesToFootAndInches(height?.value);
        const centimetres = footInchesToCentimetres(feetInches.feet, feetInches.inches);
        return `${feetInches.feet}ft ${feetInches.inches}in (${centimetres}cm)`;
    } else {
        const feetInches = centimetresToFootInches(height?.value ?? 0);
        return `${feetInches.feet}ft ${feetInches.inches}in (${height?.value}cm)`;
    }
}

function capitalize(string: string | undefined) {
    if (!string) { return ""; }
    return string.charAt(0).toUpperCase() + string.slice(1);
}

function taxPercentageOfTotalFund(total: number, taxfree: number): string {
    return ((taxfree ?? 0) / (total ?? 0) * 100).toFixed(2);
}

function CalculateWeightLine(weight: Weight) {
    if (weight?.unit === MeasurementUnitWeight.Pounds) {
        const stonePounds = convertPoundsToStoneAndPounds(weight.value);
        const kg = stonePoundsToKilograms(stonePounds.stone, stonePounds.pounds);
        return `${stonePounds.stone}st ${stonePounds.pounds}lbs (${kg}kg)`;
    } else {
        const stonePounds = kilogramsToStonePounds(weight?.value ?? 0);
        return `${stonePounds.stone}st ${stonePounds.pounds}lbs (${weight?.value}kg)`;
    }
}

function CalculateWaist(waist: Waist) {
    if (waist?.unit === MeasurementUnitLength.Inches) {
        const centimetres = inchesToCentimetres(waist?.value);
        return `${waist.value}in (${centimetres}cm)`;
    } else {
        const inches = centimetresToInches(waist?.value ?? 0);
        return `${inches}in (${waist?.value}cm)`;
    }
}

const getDailyActivitiesLabels = (
    group: ActivityLabelGroup,
    activity: string | null,
) => DailyActivitiesLabels[group][activity as keyof typeof DailyActivitiesLabels[typeof group]];

function groupedConditions(conditions: z.infer<typeof MedicalCondition>[]) {
    if (conditions) {
        return groupBy(conditions, (condition: z.infer<typeof MedicalCondition>) => condition.type);
    }
    return null;
}

function mapRelationshipToCustomer(relationship: RelationshipToCustomer | undefined): string {
    if (relationship === undefined) {
        return "";
    }

    const relationshipMap: Record<RelationshipToCustomer, string> = {
        dependant: "Dependant",
        wife: "Wife",
        husband: "Husband",
        civilPartner: "Civil Partner",
        cohabitant: "Cohabitant",
    };

    return relationshipMap[relationship] || "";
}

function isRegularSmoker(lifestyle: Lifestyle): boolean {
    if (lifestyle?.smoker !== SmokerStatus.Currently || !lifestyle?.smoking?.started) {
        return false;
    }

    const startedYear = Number(format(new Date(lifestyle?.smoking?.started), "yyyy"));
    const currentYear = Number(format(new Date(), "yyyy"));
    const numberOfYears = currentYear - startedYear;

    return numberOfYears >= 10;
}

</script>
<template>
    <div class="container p-0">
        <div class="row">
            <div class="col-12 summaryInfo">
                <h4 class="text-center blue font-weight-bold">
                    Applicant details
                </h4>
                <p id="applicantName">
                    <span>Name:</span> <span class="sessioncamhidetext">{{ quote.customer.title }} {{ quote.customer.forename }} {{ quote.customer.surname }}</span>
                </p>
                <p id="applicantPostcode">
                    <span>Postcode:</span> <span class="sessioncamhidetext">{{ quote.customer.address.postcode }}</span>
                </p>
                <p id="applicantEmail">
                    <span>Email address:</span> <span class="sessioncamhidetext">{{ quote.customer.email }}</span>
                </p>
                <p id="applicantPhone">
                    <span>Telephone:</span> <span class="sessioncamhidetext">{{ quote.customer.phone }}</span>
                </p>
                <p id="applicantDob">
                    <span>Date of birth:</span> <span class="sessioncamhidetext">{{ format(new Date(quote.customer.dob), 'dd/MM/yyyy') }}</span>
                </p>
            </div>
        </div>
        <hr class="hr-3">
        <div class="row">
            <div
                class=" col-12 disable-selection accHeader"
            >
                <p v-if="quote.quoteTime">
                    Quote date: {{ format(new Date(quote.quoteTime), 'dd/MM/yyyy HH:mm') }}
                </p>
            </div>
            <div
                class="col-12 accordion-body summaryInfo"
            >
                <h4 class="text-center blue font-weight-bold">
                    Annuity details
                </h4>
                <p v-if="quote.quoteTime">
                    <span>Annuity start date:</span> {{ format(new Date(quote.quoteTime), 'dd/MM/yyyy') }}
                </p>
                <p id="applicantTaxFreeSum">
                    <span>Tax-free lump sum:</span> {{ taxPercentageOfTotalFund(quote.product.totalFund, quote.product.taxFree.value ) }}% £{{ quote.product.taxFree.value }}
                </p>
                <p
                    v-if="quote.product.guarantee.type === 'guarantee-period'"
                    id="applicantGuaranteePeriod"
                >
                    <span>Guarantee period:</span> {{ quote.product.guarantee.years }}
                </p>
                <p id="applicantAnnualIncrease">
                    <span>Annual increase:</span> {{ capitalize(quoteEscalationType) }}
                </p>
                <p id="applicantPaymentFrequency">
                    <span>Payment frequency:</span> {{ capitalize(quote.product.paymentFrequency) }}
                </p>
                <p id="applicantIncomePayment">
                    <span>Income payment:</span> {{ capitalize(quote.product.paymentInArrears ? "in arrears" : "in advance") }}
                </p>
                <p id="applicantSpouseDependant">
                    <span>Pay to a spouse/dependant after death:</span> {{ capitalize(applicantSpouseDependant) }}
                </p>
                <p id="applicantTotalPensionFund">
                    <span>Total value of pension fund(s):</span> {{ formatTotalFund(quote.product.totalFund) }}
                </p>
                <hr class="hr-3">
                <h4 class="text-center blue font-weight-bold">
                    Applicant information
                </h4>
                <h5 class="mt-2">
                    Your lifestyle
                </h5>
                <p v-if="quote.customer.lifestyle && 'alcoholUnitsPerWeek' in quote.customer.lifestyle">
                    <span>Units of alcohol per week:</span> {{ quote.customer.lifestyle.alcoholUnitsPerWeek }}
                </p>
                <p
                    v-if="quote.customer.lifestyle && 'smoker' in quote.customer.lifestyle"
                    id="applicantRegularDailySmoker"
                >
                    <span>Regular daily smoker for the last 10 years:</span>
                    {{ YesNo(isRegularSmoker(quote.customer.lifestyle)) }}
                </p>
                <div
                    v-if="quote.customer.lifestyle
                        && 'smoker' in quote.customer.lifestyle && quote.customer.lifestyle.smoker === SmokerStatus.Currently"
                >
                    <p>
                        <span>Year you started smoking:</span> {{ format(new Date(quote.customer.lifestyle.smoking.started), 'dd/MM/yyyy') }}
                    </p>
                    <p>
                        Do you smoke the following types of tobacco:
                    </p>
                    <p id="applicantCigarettes">
                        <span>Cigarettes:</span> {{ YesNo(quote.customer.lifestyle.smoking.cigarettesPerDay > 0) }}
                    </p>
                    <p
                        v-if="quote.customer.lifestyle.smoking.cigarettesPerDay > 0"
                        id="applicantCigarettesPerDay"
                    >
                        <span>Cigarettes smoked daily:</span> {{ quote.customer.lifestyle.smoking.cigarettesPerDay }}
                    </p>
                    <p id="applicantCigars">
                        <span>Cigars:</span> {{ YesNo(quote.customer.lifestyle.smoking.cigarsPerDay > 0) }}
                    </p>
                    <p
                        v-if="quote.customer.lifestyle.smoking.cigarsPerDay > 0"
                        id="applicantCigarsPerDay"
                    >
                        <span>Cigars smoked daily:</span> {{ quote.customer.lifestyle.smoking.cigarsPerDay }}
                    </p>
                    <p id="applicantRollingTobacco">
                        <span>Rolling tobacco:</span> {{ YesNo(quote.customer.lifestyle.smoking.rollingTobaccoGramsPerWeek > 0) }}
                    </p>
                    <p
                        v-if="quote.customer.lifestyle.smoking.rollingTobaccoGramsPerWeek > 0"
                        id="applicantRollingTobaccoPerWeek"
                    >
                        <span>Rolling tobacco smoked daily:</span> {{ quote.customer.lifestyle.smoking.rollingTobaccoGramsPerWeek }}
                    </p>
                    <p id="applicantPipeTobacco">
                        <span>Pipe tobacco:</span> {{ YesNo(quote.customer.lifestyle.smoking.pipeTobaccoGramsPerWeek > 0) }}
                    </p>
                    <p
                        v-if="quote.customer.lifestyle.smoking.pipeTobaccoGramsPerWeek > 0"
                        id="applicantPipeTobaccoPerWeek"
                    >
                        <span>Pipe tobacco smoked daily:</span> {{ quote.customer.lifestyle.smoking.pipeTobaccoGramsPerWeek }}
                    </p>
                </div>
                <hr class="hr-3">
                <h5 class="mt-2">
                    Your physique
                </h5>
                <p
                    v-if="quote.customer.lifestyle && 'height' in quote.customer.lifestyle"
                    id="applicantHeight"
                >
                    <span>Your height:</span> {{ CalculateHeightLine(quote.customer.lifestyle.height) }}
                </p>
                <p
                    v-if="quote.customer.lifestyle && 'weight' in quote.customer.lifestyle"
                    id="applicantWeight"
                >
                    <span>Your weight:</span> {{ CalculateWeightLine(quote.customer.lifestyle.weight) }}
                </p>
                <p
                    v-if="quote.customer.lifestyle && 'waist' in quote.customer.lifestyle"
                    id="applicantWaist"
                >
                    <span>Your waist measurement:</span> {{ CalculateWaist(quote.customer.lifestyle.waist) }}
                </p>
                <hr class="hr-3">
                <h5 class="mt-2">
                    Your health
                </h5>
                <p><span>Any medical conditions:</span> {{ YesNo(quote.customer.medical && quote.customer.medical.length > 0) }}</p>
                <div v-if="quote.customer.medical && quote.customer.medical.length > 0 && quote.customer.dailyActivities">
                    <h5 class="mt-2">
                        Your daily activities
                    </h5>
                    <p id="applicantDressYourself">
                        <span>Dressing:</span> {{
                            getDailyActivitiesLabels("dressYourself", quote.customer.dailyActivities.dressYourself)
                        }}
                    </p>
                    <p id="applicantMobility">
                        <span>Mobility:</span> {{
                            getDailyActivitiesLabels(
                                "applicantMobility",
                                quote.customer.dailyActivities.applicantMobility
                            )
                        }}
                    </p>
                    <p id="applicantTransferring">
                        <span>Transferring:</span> {{
                            getDailyActivitiesLabels(
                                "applicantTransferring",
                                quote.customer.dailyActivities.applicantTransferring
                            )
                        }}
                    </p>
                    <p id="applicantBladder">
                        <span>Bladder:</span> {{
                            getDailyActivitiesLabels(
                                "applicantBladder",
                                quote.customer.dailyActivities.applicantBladder
                            )
                        }}
                    </p>
                    <p id="applicantBowels">
                        <span>Bowels:</span> {{
                            getDailyActivitiesLabels("applicantBowels", quote.customer.dailyActivities.applicantBowels)
                        }}
                    </p>
                    <p id="applicantBathing">
                        <span>Bathing:</span> {{
                            getDailyActivitiesLabels(
                                "applicantBathing",
                                quote.customer.dailyActivities.applicantBathing
                            )
                        }}
                    </p>
                    <p id="applicantFeeding">
                        <span>Feeding:</span> {{
                            getDailyActivitiesLabels(
                                "applicantFeeding",
                                quote.customer.dailyActivities.applicantFeeding
                            )
                        }}
                    </p>
                    <p id="applicantProgression">
                        <span>Progression in the last 5 years:</span> {{
                            getDailyActivitiesLabels(
                                "applicantProgression",
                                quote.customer.dailyActivities.applicantProgression
                            )
                        }}
                    </p>
                    <h5
                        v-if="quote.customer.medical.length > 0"
                        class="mt-2"
                    >
                        Medical Conditions
                    </h5>
                    <div v-if="quote.customer.medical.length > 0">
                        <div
                            v-for="(group, i) in groupedConditions(quote.customer.medical)"
                            id="applicantGroupedConditions"
                            :key="i"
                            class="sessioncamhidetext"
                        >
                            <span>{{ group[0].type }} conditions: </span>
                            <span
                                v-for="(condition, c) in group"
                                :key="c"
                                class="col-md-8"
                            >
                                {{ capitalize(condition.name) }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <template v-if="quote.dependant && (typeof quote.product.paySurvivingDependant.type === typeof PaySurvivingDependantType.Fraction)">
                <hr
                    class="hr-3 mt-3"
                >
                <div class="summaryInfo m-0 p-0">
                    <h4 class="text-center blue font-weight-bold">
                        Dependant details
                    </h4>
                    <h5 class="mt-2">
                        Their personal details
                    </h5>
                    <p
                        id="dependantName"
                    >
                        <span>Name:</span> <span class="sessioncamhidetext">{{ capitalize(quote.dependant.title) }} {{ capitalize(quote.dependant.forename) }} {{ capitalize(quote.dependant.surname) }}</span>
                    </p>
                    <p id="dependantDob">
                        <span>Date of birth:</span> <span class="sessioncamhidetext">{{ format(new Date(quote.dependant.dob), 'dd/MM/yyyy') }}</span>
                    </p>
                    <p id="dependantPostcode">
                        <span>Postcode:</span> <span class="sessioncamhidetext">{{ quote.dependant.address.postcode }}</span>
                    </p>
                    <p id="dependantRelationship">
                        <span>Dependant relationship:</span> {{ mapRelationshipToCustomer(quote.dependant.relationshipToCustomer) }}
                    </p>
                    <hr class="hr-3">
                    <h5 class="mt-2">
                        Their physique
                    </h5>
                    <p
                        v-if="quote.dependant.lifestyle && 'height' in quote.dependant.lifestyle"
                        id="dependantHeight"
                    >
                        <span>Their height:</span> {{ CalculateHeightLine(quote.dependant.lifestyle.height) }}
                    </p>
                    <p
                        v-if="quote.dependant.lifestyle && 'weight' in quote.dependant.lifestyle"
                        id="dependantWeight"
                    >
                        <span>Their weight:</span> {{ CalculateWeightLine(quote.dependant.lifestyle.weight) }}
                    </p>
                    <p
                        v-if="quote.dependant.lifestyle && 'waist' in quote.dependant.lifestyle"
                        id="dependantWaist"
                    >
                        <span>Their waist measurement:</span> {{ CalculateWaist(quote.dependant.lifestyle.waist) }}
                    </p>
                    <hr class="hr-3">
                    <h5 class="mt-2">
                        Their health
                    </h5>
                    <p><span>Their medical conditions:</span> {{ YesNo(quote.dependant.medical && quote.dependant.medical.length > 0) }}</p>
                    <div v-if="quote.dependant.medical && quote.dependant.medical.length > 0 && quote.dependant.dailyActivities">
                        <h5 class="mt-2">
                            Their daily activities
                        </h5>
                        <p
                            id="dependantDressYourself"
                        >
                            <span>Dressing:</span> {{
                                getDailyActivitiesLabels("dressYourself", quote.dependant.dailyActivities.dressYourself)
                            }}
                        </p>
                        <p id="dependantMobility">
                            <span>Mobility:</span> {{
                                getDailyActivitiesLabels(
                                    "applicantMobility",
                                    quote.dependant.dailyActivities.applicantMobility
                                )
                            }}
                        </p>
                        <p id="dependantTransferring">
                            <span>Transferring:</span> {{
                                getDailyActivitiesLabels(
                                    "applicantTransferring",
                                    quote.dependant.dailyActivities.applicantTransferring
                                )
                            }}
                        </p>
                        <p id="dependantBladder">
                            <span>Bladder:</span> {{
                                getDailyActivitiesLabels(
                                    "applicantBladder",
                                    quote.dependant.dailyActivities.applicantBladder
                                )
                            }}
                        </p>
                        <p id="dependantBowels">
                            <span>Bowels:</span> {{
                                getDailyActivitiesLabels(
                                    "applicantBowels",
                                    quote.dependant.dailyActivities.applicantBowels
                                )
                            }}
                        </p>
                        <p id="dependantBathing">
                            <span>Bathing:</span> {{
                                getDailyActivitiesLabels(
                                    "applicantBathing",
                                    quote.dependant.dailyActivities.applicantBathing
                                )
                            }}
                        </p>
                        <p id="dependantFeeding">
                            <span>Feeding:</span> {{
                                getDailyActivitiesLabels(
                                    "applicantFeeding",
                                    quote.dependant.dailyActivities.applicantFeeding
                                )
                            }}
                        </p>
                        <p id="dependantProgression">
                            <span>Progression in the last 5 years:</span> {{
                                getDailyActivitiesLabels(
                                    "applicantProgression",
                                    quote.dependant.dailyActivities.applicantProgression
                                )
                            }}
                        </p>
                        <h5
                            v-if="quote.dependant.medical.length > 0"
                            class="mt-2"
                        >
                            Their medical Conditions
                        </h5>
                        <div v-if="quote.dependant.medical.length > 0">
                            <div
                                v-for="(group, i) in groupedConditions(quote.dependant.medical)"
                                id="dependantGroupedConditions"
                                :key="i"
                                class="sessioncamhidetext"
                            >
                                <span>{{ group[0].type }} conditions: </span>
                                <span
                                    v-for="(condition, c) in group"
                                    :key="c"
                                    class="col-md-8"
                                >
                                    {{ condition.name }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <hr class="hr-3">
                    <div>
                        <h5 class="mt-2">
                            Their lifestyle
                        </h5>
                        <p v-if="quote.dependant.lifestyle && 'alcoholUnitsPerWeek' in quote.dependant.lifestyle">
                            <span>Units of alcohol per week:</span> {{ quote.dependant.lifestyle.alcoholUnitsPerWeek }}
                        </p>
                        <p
                            v-if="quote.dependant.lifestyle && 'smoker' in quote.dependant.lifestyle"
                            id="dependantRegularDailySmoker"
                        >
                            <span>Regular daily smoker for the last 10 years:</span>
                            {{ YesNo(isRegularSmoker(quote.dependant.lifestyle)) }}
                        </p>
                        <div
                            v-if="quote.dependant.lifestyle
                                && 'smoker' in quote.dependant.lifestyle && quote.dependant.lifestyle.smoker === SmokerStatus.Currently"
                        >
                            <p>
                                <span>Year you started smoking:</span> {{ format(new Date(quote.dependant.lifestyle.smoking.started), 'dd/MM/yyyy') }}
                            </p>
                            <p>
                                Do you smoke the following types of tobacco:
                            </p>
                            <p id="dependantCigarettes">
                                <span>Cigarettes:</span> {{ YesNo(quote.dependant.lifestyle.smoking.cigarettesPerDay > 0) }}
                            </p>
                            <p
                                v-if="quote.dependant.lifestyle.smoking.cigarettesPerDay > 0"
                                id="dependantCigarettesPerDay"
                            >
                                <span>Cigarettes smoked daily:</span> {{ quote.dependant.lifestyle.smoking.cigarettesPerDay }}
                            </p>
                            <p id="dependantCigars">
                                <span>Cigars:</span> {{ YesNo(quote.dependant.lifestyle.smoking.cigarsPerDay > 0) }}
                            </p>
                            <p
                                v-if="quote.dependant.lifestyle.smoking.cigarsPerDay > 0"
                                id="dependantCigarsPerDay"
                            >
                                <span>Cigars smoked daily:</span> {{ quote.dependant.lifestyle.smoking.cigarsPerDay }}
                            </p>
                            <p id="dependantRollingTobacco">
                                <span>Rolling tobacco:</span> {{ YesNo(quote.dependant.lifestyle.smoking.rollingTobaccoGramsPerWeek > 0) }}
                            </p>
                            <p
                                v-if="quote.dependant.lifestyle.smoking.rollingTobaccoGramsPerWeek > 0"
                                id="dependantRollingTobaccoPerWeek"
                            >
                                <span>Rolling tobacco smoked daily:</span> {{ quote.dependant.lifestyle.smoking.rollingTobaccoGramsPerWeek }}
                            </p>
                            <p id="dependantPipeTobacco">
                                <span>Pipe tobacco:</span> {{ YesNo(quote.dependant.lifestyle.smoking.pipeTobaccoGramsPerWeek > 0) }}
                            </p>
                            <p
                                v-if="quote.dependant.lifestyle.smoking.pipeTobaccoGramsPerWeek > 0"
                                id="dependantPipeTobaccoPerWeek"
                            >
                                <span>Pipe tobacco smoked daily:</span> {{ quote.dependant.lifestyle.smoking.pipeTobaccoGramsPerWeek }}
                            </p>
                        </div>
                    </div>
                </div>
            </template>
            <hr class="hr-3 mt-3">
            <div class="col-12 small accordion-body">
                <h4 class="text-center">
                    Full quote
                </h4>
                <p class="text-secondary">
                    This is a summary of your quote. Please also read your full quotes in the providers quote (key facts) documents.
                </p>
                <br>
                <p class="text-secondary">
                    To access these, close this page and click on ‘More info’ for each provider on the results page. You can then view the quote (key facts) document for each available provider under 'Important documents'.
                </p>
            </div>
        </div>
    </div>
</template>
<style>
    .accHeader{
        margin-top: 30px;
        margin-bottom: 30px;
        color: #1b6f7e;
        text-align: center;
    }
</style>
